// import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";

// /**
//  * Exports the SPC view to an .xlsx laid out EXACTLY like the reference
//  * "SPC_Housing.xlsx" sheet:
//  *   Title block -> Header info (Part/Instrument/Machine + IMP note) ->
//  *   X-Chart | R-Chart | Histogram (side by side, with USL/LSL/DIFF panel) ->
//  *   Calculation for Cp/Cpk block -> Graphical UCL/LCL rows ->
//  *   Verdict banners (Cp / Cpk) -> raw SHIFT/DATE/X1-X5/SX/X-bar/R grid (25 cols) ->
//  *   footer.
//  *
//  * @param {object} data - the full spc-data API response { product, spc, meta, date }
//  */
// export async function exportSpcToExcel(data) {
//   const { product, spc, meta, date } = data;
//   if (!spc || spc.insufficientData) {
//     throw new Error(
//       "Cannot export — Cp/Cpk not yet calculated (need 50+ readings).",
//     );
//   }

//   // ---- derive USL / LSL from the spec string (e.g. "370 ±10gm") ----
//   // Falls back to product.usl / product.lsl if the API already provides them.
//   function deriveLimits() {
//     if (product.usl != null && product.lsl != null) {
//       return { usl: Number(product.usl), lsl: Number(product.lsl) };
//     }
//     const spec = String(product.specification || "");
//     const m = spec.match(/(-?\d+(?:\.\d+)?)\s*±\s*(-?\d+(?:\.\d+)?)/);
//     if (m) {
//       const nominal = parseFloat(m[1]);
//       const tol = parseFloat(m[2]);
//       return { usl: nominal + tol, lsl: nominal - tol };
//     }
//     // last resort: use the computed control limits so the sheet still renders
//     return {
//       usl: spc.controlLimits?.xBarUCL ?? 0,
//       lsl: spc.controlLimits?.xBarLCL ?? 0,
//     };
//   }
//   const { usl, lsl } = deriveLimits();
//   const diff = usl - lsl;
//   const rangeFor12 = diff / 12;

//   const wb = new ExcelJS.Workbook();
//   const ws = wb.addWorksheet("SPC Study", {
//     views: [{ showGridLines: false }],
//   });

//   // ---- style helpers ----
//   const thin = { style: "thin", color: { argb: "FF888888" } };
//   const dbl = { style: "double", color: { argb: "FF444444" } };
//   const border = { top: thin, left: thin, bottom: thin, right: thin };
//   const cellFont = { name: "Arial", size: 10 };
//   const boldFont = { name: "Arial", size: 10, bold: true };
//   const green = "FFCCFFCC";
//   const cyan = "FFCCFFFF";
//   const yellow = "FFFCF9DA";
//   const impFill = "FFCCFFCC";

//   const NUM_SUBGROUPS = spc.subgroups.length; // typically 25
//   const lastCol = 1 + NUM_SUBGROUPS; // col A = label, then one per subgroup

//   // column widths: A narrow label col, B..Z ~ data cols
//   ws.getColumn(1).width = 10;
//   for (let c = 2; c <= lastCol; c++) ws.getColumn(c).width = 9;

//   function set(row, col, value, opts = {}) {
//     const cell = ws.getCell(row, col);
//     cell.value = value;
//     cell.font = opts.font || cellFont;
//     cell.border = opts.border || border;
//     cell.alignment = opts.align || { horizontal: "center", vertical: "middle" };
//     if (opts.fill) {
//       cell.fill = {
//         type: "pattern",
//         pattern: "solid",
//         fgColor: { argb: opts.fill },
//       };
//     }
//     if (opts.numFmt) cell.numFmt = opts.numFmt;
//     return cell;
//   }

//   function merge(r1, c1, r2, c2) {
//     ws.mergeCells(r1, c1, r2, c2);
//     return ws.getCell(r1, c1);
//   }

//   // ============================================================
//   // TITLE BLOCK (rows 1-3)
//   // ============================================================
//   merge(1, 1, 3, 4); // decorative corner box
//   ws.getCell(1, 1).border = { top: dbl, left: dbl, bottom: thin, right: thin };

//   const titleCell = merge(1, 5, 3, 22);
//   titleCell.value = "STATISTICAL PROCESS CONTROL STUDY";
//   titleCell.font = { name: "Arial", size: 20, bold: true };
//   titleCell.alignment = {
//     horizontal: "center",
//     vertical: "middle",
//     wrapText: true,
//   };
//   titleCell.border = { top: dbl, bottom: thin, left: thin, right: thin };

//   merge(1, 23, 1, 26).value = `FORMAT No.:-  ${meta.formatNo || "-"}`;
//   merge(2, 23, 2, 26).value = `CL.No.   : ${meta.clNo || "-"}`;
//   merge(3, 23, 3, 26).value = `Page No. ${meta.pageNo || "1 of 1"}`;
//   [1, 2, 3].forEach((r) => {
//     const c = ws.getCell(r, 23);
//     c.font = cellFont;
//     c.alignment = { horizontal: "left", vertical: "middle" };
//     c.border = {
//       top: r === 1 ? dbl : thin,
//       bottom: thin,
//       left: thin,
//       right: dbl,
//     };
//   });

//   // ============================================================
//   // HEADER INFO BLOCK (rows 4-6)
//   // ============================================================
//   const headerRows = [
//     [
//       "PART NAME:",
//       product.name,
//       "INSTRUMENT:",
//       meta.instrument,
//       "L.COUNT:",
//       meta.leastCount,
//     ],
//     [
//       "PART NO.:",
//       product.code,
//       "SPECIFICATION:",
//       product.specification,
//       "MACHINE:",
//       meta.machine,
//     ],
//     [
//       `SAMPLE SIZE:`,
//       `${spc.count}`,
//       "OPERATION:",
//       meta.operation,
//       "DOC.NO./DATE:",
//       date,
//     ],
//   ];
//   let r = 4;
//   headerRows.forEach((row) => {
//     // label/value pairs: A:B label, C:F value | G:H label, I:L value | M:N label, O:S value
//     merge(r, 1, r, 2).value = row[0];
//     merge(r, 3, r, 6).value = row[1];
//     merge(r, 7, r, 8).value = row[2];
//     merge(r, 9, r, 12).value = row[3];
//     merge(r, 13, r, 14).value = row[4];
//     merge(r, 15, r, 19).value = row[5];
//     [1, 3, 7, 9, 13, 15].forEach((c) => {
//       const cell = ws.getCell(r, c);
//       cell.font = c === 3 || c === 9 || c === 15 ? cellFont : boldFont;
//       cell.border = { ...border, left: c === 1 ? dbl : thin };
//       cell.alignment = { horizontal: "left", vertical: "middle" };
//       if (c === 3 || c === 9 || c === 15) {
//         cell.fill = {
//           type: "pattern",
//           pattern: "solid",
//           fgColor: { argb: cyan },
//         };
//       }
//     });
//     r++;
//   });
//   // IMP note block, spans the header rows on the right
//   const impCell = merge(4, 20, 6, 26);
//   impCell.value = meta.impNote || "";
//   impCell.font = { name: "Arial", size: 9, italic: true };
//   impCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
//   impCell.fill = {
//     type: "pattern",
//     pattern: "solid",
//     fgColor: { argb: impFill },
//   };
//   impCell.border = { top: thin, bottom: thin, left: thin, right: dbl };

//   // ============================================================
//   // ROW 7-8: X-CHART / R-CHART / HISTOGRAM headers + USL/LSL panel
//   // ============================================================
//   r = 7;
//   merge(r, 1, r + 1, 3).value = "X - CHART";
//   merge(r, 14, r + 1, 16).value = "R - CHART";
//   merge(r, 17, r + 1, 19).value = "HISTOGRAM";
//   [1, 14, 17].forEach((c) => {
//     const cell = ws.getCell(r, c);
//     cell.font = { name: "Arial", size: 10, bold: true };
//     cell.border = { ...border, left: c === 1 ? dbl : thin };
//   });

//   merge(r, 20, r, 21).value = "USL";
//   set(r, 22, usl);
//   merge(r, 23, r + 1, 23).value = "DIFF.";
//   merge(r, 24, r + 1, 24).value = diff;
//   merge(r, 25, r + 1, 25).value = "RANGE FOR 12";
//   merge(r, 26, r + 1, 26).value = Math.round(rangeFor12 * 1e6) / 1e6;
//   merge(r + 1, 20, r + 1, 21).value = "LSL";
//   set(r + 1, 22, lsl);

//   // sub-header row (row 9): UCL/Xbar/LCL | UCL/r/LCL | RANGE/FREQ
//   r = 9;
//   ["UCL", "Xbar", "LCL"].forEach((h, i) =>
//     set(r, 1 + i, h, { font: boldFont, fill: yellow }),
//   );
//   ["UCL", "r", "LCL"].forEach((h, i) =>
//     set(r, 14 + i, h, { font: boldFont, fill: yellow }),
//   );
//   merge(r, 17, r + 1, 17).value = "RANGE";
//   merge(r, 19, r + 1, 19).value = "FREQ.";
//   ws.getCell(r, 17).font = boldFont;
//   ws.getCell(r, 19).font = boldFont;
//   r = 10;
//   set(r, 17, "FIRST", { font: boldFont, fill: yellow });
//   set(r, 18, "LAST", { font: boldFont, fill: yellow });

//   // ============================================================
//   // ROWS 10-34 (25 rows): X-Chart + R-Chart data, Histogram bins (10-22)
//   // ============================================================
//   const xRows = spc.xChartRows || [];
//   const rRows = spc.rChartRows || [];
//   const bins = spc.histogram?.bins || [];
//   for (let i = 0; i < 25; i++) {
//     const row = 10 + i;
//     if (xRows[i]) {
//       set(row, 1, round3(xRows[i].ucl), { border: { ...border, left: dbl } });
//       set(row, 2, round3(xRows[i].xBar));
//       set(row, 3, round3(xRows[i].lcl));
//     }
//     if (rRows[i]) {
//       set(row, 14, round3(rRows[i].ucl));
//       set(row, 15, round3(rRows[i].r));
//       set(row, 16, round3(rRows[i].lcl));
//     }
//     if (bins[i]) {
//       set(row, 17, round3(bins[i].first));
//       set(row, 18, round3(bins[i].last));
//       set(row, 19, bins[i].freq);
//     }
//   }

//   // ============================================================
//   // CALCULATION FOR Cp/Cpk block (cols 20-26, starting row 23)
//   // ============================================================
//   merge(23, 20, 24, 26).value = "CALCULATION FOR Cp/Cpk";
//   ws.getCell(23, 20).font = { name: "Arial", size: 14, bold: true };
//   ws.getCell(23, 20).border = { ...border, right: dbl };
//   ws.getCell(23, 20).alignment = { horizontal: "center", vertical: "middle" };

//   merge(25, 20, 25, 26).value = `FOR  N - SAMPLE SIZE = ${spc.constantsUsed.N}`;
//   ws.getCell(25, 20).font = cellFont;
//   ws.getCell(25, 20).alignment = { horizontal: "left", vertical: "middle" };
//   ws.getCell(25, 20).border = { ...border, right: dbl };

//   merge(26, 20, 27, 21).value = "CONSTANT :";
//   ws.getCell(26, 20).alignment = {
//     wrapText: true,
//     horizontal: "center",
//     vertical: "middle",
//   };
//   const constLabels = ["A2", "D3", "D4", "d2"];
//   constLabels.forEach((k, i) => {
//     set(26, 22 + i, k, { font: boldFont, fill: yellow });
//     set(27, 22 + i, spc.constantsUsed[k]);
//   });

//   merge(29, 20, 29, 21).value = " X  =";
//   merge(29, 22, 29, 23).value = round4(spc.xBarBar);
//   merge(29, 24, 29, 24).value = " R =";
//   merge(29, 25, 29, 26).value = round3(spc.rBar);

//   merge(31, 20, 31, 21).value = " s = R / d2";
//   set(31, 22, round3(spc.rBar));
//   merge(31, 23, 31, 24).value = round4(spc.sigma);
//   merge(31, 20 + 5, 31, 21 + 5).value = ` A2.R =   ${spc.constantsUsed.A2} X `;

//   merge(33, 20, 33, 21).value = " UCLX = X + A2.R";
//   merge(33, 22, 33, 23).value = round4(spc.controlLimits.xBarUCL);
//   merge(33, 24, 33, 25).value = " UCLR = D4 .R";
//   merge(33, 26, 33, 26).value = round4(spc.controlLimits.rUCL);

//   merge(35, 20, 35, 21).value = " LCLX = X - A2.R";
//   merge(35, 22, 35, 23).value = round4(spc.controlLimits.xBarLCL);
//   merge(35, 24, 35, 25).value = " LCLR = D3 .R";
//   merge(35, 26, 35, 26).value = round4(spc.controlLimits.rLCL);

//   // graphical UCL/LCL rows (left side, cols 1-2 and 14-15)
//   merge(35, 1, 35, 2).value = "Graphical X  UCL";
//   set(35, 3, round4(spc.controlLimits.xBarUCL), {
//     border: { ...border, left: dbl },
//   });
//   merge(36, 1, 36, 2).value = "Graphical X  LCL";
//   set(36, 3, round4(spc.controlLimits.xBarLCL), {
//     border: { ...border, left: dbl },
//   });
//   merge(35, 14, 35, 15).value = "Graphical R  UCL";
//   set(35, 16, round4(spc.controlLimits.rUCL));
//   merge(36, 14, 36, 15).value = "Graphical R  LCL";
//   set(36, 16, round4(spc.controlLimits.rLCL));

//   // ============================================================
//   // VERDICT BANNERS (rows 37-42)
//   // ============================================================
//   const cpColor = spc.cp >= 1.33 ? "FF1B7A3D" : "FFB00020";
//   const cpkColor = spc.cpk >= 1.33 ? "FF1B7A3D" : "FFB00020";

//   const cpBanner = merge(37, 1, 38, 10);
//   cpBanner.value =
//     spc.cpVerdict ||
//     `PROCESS CAPABILITY IS ${spc.cp >= 1.33 ? "GOOD" : "IMPROVEMENT REQUIRED"}`;
//   cpBanner.font = {
//     name: "Arial",
//     size: 18,
//     bold: true,
//     color: { argb: cpColor },
//   };
//   cpBanner.alignment = { horizontal: "center", vertical: "middle" };
//   cpBanner.border = { ...border, left: dbl };

//   merge(37, 11, 38, 13).value = "Cp";
//   ws.getCell(37, 11).font = { name: "Arial", size: 18, bold: true };
//   merge(37, 14, 38, 16).value = round3(spc.cp);
//   ws.getCell(37, 14).font = { name: "Arial", size: 16, bold: true };

//   merge(37, 17, 38, 19).value = " Cp = T/ 6s";
//   merge(37, 20, 38, 21).value = round3(spc.cp);
//   merge(37, 22, 38, 25).value = " T = TOTAL TOLERANCE  ( USL-LSL )";
//   merge(37, 26, 38, 26).value = diff;

//   const cpkBanner = merge(39, 1, 40, 10);
//   cpkBanner.value =
//     spc.cpkVerdict ||
//     `PROCESS CAPABILITY INDEX IS ${spc.cpk >= 1.33 ? "GOOD" : "IMPROVEMENT REQUIRED"}`;
//   cpkBanner.font = {
//     name: "Arial",
//     size: 18,
//     bold: true,
//     color: { argb: cpkColor },
//   };
//   cpkBanner.alignment = { horizontal: "center", vertical: "middle" };
//   cpkBanner.border = { ...border, left: dbl };

//   merge(39, 11, 40, 13).value = "Cpk";
//   ws.getCell(39, 11).font = { name: "Arial", size: 18, bold: true };
//   merge(39, 14, 40, 16).value = round3(spc.cpk);
//   ws.getCell(39, 14).font = { name: "Arial", size: 16, bold: true };

//   merge(39, 17, 40, 19).value = "Cpk = ( USL X - X )/ 3s";
//   merge(39, 20, 40, 21).value = round3(spc.cpk);
//   merge(39, 22, 42, 26).value =
//     "FOR Cpk TAKE MINIMUM VALUE FOR BOTH SIDE TOLERANCE & FOR SINGLE SIDE TOLERANCE DO NOT CONSIDER MINIMUM VALUE.";
//   ws.getCell(39, 22).alignment = { wrapText: true, vertical: "middle" };
//   ws.getCell(39, 22).font = { name: "Arial", size: 9 };

//   merge(41, 17, 42, 19).value = "Cpk = ( X - LSL X )/ 3s";
//   merge(41, 20, 42, 21).value = round3(spc.cpk);

//   // ============================================================
//   // RAW DATA GRID: # / SHIFT / DATE / X1-X5 / SX / X-bar / R (rows 43-53)
//   // ============================================================
//   const gridStart = 43;
//   set(gridStart, 1, "", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((_, i) => {
//     set(gridStart, i + 2, i + 1, { font: boldFont });
//   });

//   set(gridStart + 1, 1, "SHIFT", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((_, i) =>
//     set(gridStart + 1, i + 2, "1", { fill: green }),
//   );

//   set(gridStart + 2, 1, "DATE", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((_, i) =>
//     set(gridStart + 2, i + 2, date, { fill: green }),
//   );

//   for (let x = 0; x < 5; x++) {
//     set(gridStart + 3 + x, 1, `X${x + 1}`, {
//       border: { ...border, left: dbl },
//     });
//     spc.subgroups.forEach((sg, i) => {
//       set(gridStart + 3 + x, i + 2, sg.values[x] ?? "", {
//         fill: cyan,
//         font: { name: "Arial", size: 9 },
//         numFmt: "0.00",
//       });
//     });
//   }

//   set(gridStart + 8, 1, "SX", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((sg, i) => {
//     const sx =
//       sg.mean !== null
//         ? Math.round(sg.mean * sg.values.length * 1000) / 1000
//         : "";
//     set(gridStart + 8, i + 2, sx, { numFmt: "0.00" });
//   });

//   set(gridStart + 9, 1, "X", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((sg, i) => {
//     set(gridStart + 9, i + 2, sg.mean !== null ? round3(sg.mean) : "");
//   });

//   const lastGridRow = gridStart + 10;
//   set(lastGridRow, 1, "R", { border: { ...border, left: dbl, bottom: dbl } });
//   spc.subgroups.forEach((sg, i) => {
//     set(lastGridRow, i + 2, sg.range !== null ? round3(sg.range) : "", {
//       border: {
//         ...border,
//         bottom: dbl,
//         right: i === spc.subgroups.length - 1 ? dbl : thin,
//       },
//     });
//   });

//   // ============================================================
//   // FOOTER
//   // ============================================================
//   const footerRow = lastGridRow + 1;
//   merge(footerRow, 1, footerRow, lastCol).value =
//     "MP/QA/20                    Rev:00                        Eff.Date: 01.DEC.2015      ";
//   ws.getCell(footerRow, 1).font = { name: "Arial", size: 9 };
//   ws.getCell(footerRow, 1).alignment = { horizontal: "right" };
//   ws.getCell(footerRow, 1).border = {};

//   // ---- helpers ----
//   function round3(v) {
//     return v == null || v === "" ? "" : Math.round(v * 1000) / 1000;
//   }
//   function round4(v) {
//     return v == null || v === "" ? "" : Math.round(v * 10000) / 10000;
//   }

//   // ── Trigger download ──
//   const buf = await wb.xlsx.writeBuffer();
//   const filename = `SPC_${product.code}_${date}.xlsx`;
//   saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
// }

// import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";

// /**
//  * Exports the SPC view to an .xlsx laid out EXACTLY like the reference
//  * "SPC_Housing.xlsx" sheet:
//  *   Title block -> Header info (Part/Instrument/Machine + IMP note) ->
//  *   X-Chart | R-Chart | Histogram (side by side, with USL/LSL/DIFF panel) ->
//  *   Calculation for Cp/Cpk block -> Graphical UCL/LCL rows ->
//  *   Verdict banners (Cp / Cpk) -> raw SHIFT/DATE/X1-X5/SX/X-bar/R grid (25 cols) ->
//  *   footer.
//  *
//  * @param {object} data - the full spc-data API response { product, spc, meta, date }
//  */
// export async function exportSpcToExcel(data) {
//   const { product, spc, meta, date } = data;
//   if (!spc || spc.insufficientData) {
//     throw new Error(
//       "Cannot export — Cp/Cpk not yet calculated (need 50+ readings).",
//     );
//   }

//   // ---- derive USL / LSL from the spec string (e.g. "370 ±10gm") ----
//   // Falls back to product.usl / product.lsl if the API already provides them.
//   function deriveLimits() {
//     if (product.usl != null && product.lsl != null) {
//       return { usl: Number(product.usl), lsl: Number(product.lsl) };
//     }
//     const spec = String(product.specification || "");
//     const m = spec.match(/(-?\d+(?:\.\d+)?)\s*±\s*(-?\d+(?:\.\d+)?)/);
//     if (m) {
//       const nominal = parseFloat(m[1]);
//       const tol = parseFloat(m[2]);
//       return { usl: nominal + tol, lsl: nominal - tol };
//     }
//     // last resort: use the computed control limits so the sheet still renders
//     return {
//       usl: spc.controlLimits?.xBarUCL ?? 0,
//       lsl: spc.controlLimits?.xBarLCL ?? 0,
//     };
//   }
//   const { usl, lsl } = deriveLimits();
//   const diff = usl - lsl;
//   const rangeFor12 = diff / 12;

//   const wb = new ExcelJS.Workbook();
//   const ws = wb.addWorksheet("SPC Study", {
//     views: [{ showGridLines: false }],
//   });

//   // ---- style helpers ----
//   const thin = { style: "thin", color: { argb: "FF888888" } };
//   const dbl = { style: "double", color: { argb: "FF444444" } };
//   const border = { top: thin, left: thin, bottom: thin, right: thin };
//   const cellFont = { name: "Arial", size: 10 };
//   const boldFont = { name: "Arial", size: 10, bold: true };
//   const green = "FFCCFFCC";
//   const cyan = "FFCCFFFF";
//   const yellow = "FFFCF9DA";
//   const impFill = "FFCCFFCC";

//   const NUM_SUBGROUPS = spc.subgroups.length; // typically 25
//   const lastCol = 1 + NUM_SUBGROUPS; // col A = label, then one per subgroup

//   // column widths: A narrow label col, B..Z ~ data cols
//   ws.getColumn(1).width = 10;
//   for (let c = 2; c <= lastCol; c++) ws.getColumn(c).width = 9;

//   function set(row, col, value, opts = {}) {
//     const cell = ws.getCell(row, col);
//     cell.value = value;
//     cell.font = opts.font || cellFont;
//     cell.border = opts.border || border;
//     cell.alignment = opts.align || { horizontal: "center", vertical: "middle" };
//     if (opts.fill) {
//       cell.fill = {
//         type: "pattern",
//         pattern: "solid",
//         fgColor: { argb: opts.fill },
//       };
//     }
//     if (opts.numFmt) cell.numFmt = opts.numFmt;
//     return cell;
//   }

//   function merge(r1, c1, r2, c2) {
//     ws.mergeCells(r1, c1, r2, c2);
//     return ws.getCell(r1, c1);
//   }

//   // ============================================================
//   // TITLE BLOCK (rows 1-3)
//   // ============================================================
//   merge(1, 1, 3, 4); // decorative corner box
//   ws.getCell(1, 1).border = { top: dbl, left: dbl, bottom: thin, right: thin };

//   const titleCell = merge(1, 5, 3, 22);
//   titleCell.value = "STATISTICAL PROCESS CONTROL STUDY";
//   titleCell.font = { name: "Arial", size: 20, bold: true };
//   titleCell.alignment = {
//     horizontal: "center",
//     vertical: "middle",
//     wrapText: true,
//   };
//   titleCell.border = { top: dbl, bottom: thin, left: thin, right: thin };

//   merge(1, 23, 1, 26).value = `FORMAT No.:-  ${meta.formatNo || "-"}`;
//   merge(2, 23, 2, 26).value = `CL.No.   : ${meta.clNo || "-"}`;
//   merge(3, 23, 3, 26).value = `Page No. ${meta.pageNo || "1 of 1"}`;
//   [1, 2, 3].forEach((r) => {
//     const c = ws.getCell(r, 23);
//     c.font = cellFont;
//     c.alignment = { horizontal: "left", vertical: "middle" };
//     c.border = {
//       top: r === 1 ? dbl : thin,
//       bottom: thin,
//       left: thin,
//       right: dbl,
//     };
//   });

//   // ============================================================
//   // HEADER INFO BLOCK (rows 4-6)
//   // ============================================================
//   const headerRows = [
//     [
//       "PART NAME:",
//       product.name,
//       "INSTRUMENT:",
//       meta.instrument,
//       "L.COUNT:",
//       meta.leastCount,
//     ],
//     [
//       "PART NO.:",
//       product.code,
//       "SPECIFICATION:",
//       product.specification,
//       "MACHINE:",
//       meta.machine,
//     ],
//     [
//       `SAMPLE SIZE:`,
//       `${spc.count}`,
//       "OPERATION:",
//       meta.operation,
//       "DOC.NO./DATE:",
//       date,
//     ],
//   ];
//   let r = 4;
//   headerRows.forEach((row) => {
//     // label/value pairs: A:B label, C:F value | G:H label, I:L value | M:N label, O:S value
//     merge(r, 1, r, 2).value = row[0];
//     merge(r, 3, r, 6).value = row[1];
//     merge(r, 7, r, 8).value = row[2];
//     merge(r, 9, r, 12).value = row[3];
//     merge(r, 13, r, 14).value = row[4];
//     merge(r, 15, r, 19).value = row[5];
//     [1, 3, 7, 9, 13, 15].forEach((c) => {
//       const cell = ws.getCell(r, c);
//       cell.font = c === 3 || c === 9 || c === 15 ? cellFont : boldFont;
//       cell.border = { ...border, left: c === 1 ? dbl : thin };
//       cell.alignment = { horizontal: "left", vertical: "middle" };
//       if (c === 3 || c === 9 || c === 15) {
//         cell.fill = {
//           type: "pattern",
//           pattern: "solid",
//           fgColor: { argb: cyan },
//         };
//       }
//     });
//     r++;
//   });
//   // IMP note block, spans the header rows on the right
//   const impCell = merge(4, 20, 6, 26);
//   impCell.value = meta.impNote || "";
//   impCell.font = { name: "Arial", size: 9, italic: true };
//   impCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
//   impCell.fill = {
//     type: "pattern",
//     pattern: "solid",
//     fgColor: { argb: impFill },
//   };
//   impCell.border = { top: thin, bottom: thin, left: thin, right: dbl };

//   // ============================================================
//   // ROW 7-8: X-CHART / R-CHART / HISTOGRAM headers + USL/LSL panel
//   // ============================================================
//   r = 7;
//   merge(r, 1, r + 1, 3).value = "X - CHART";
//   merge(r, 14, r + 1, 16).value = "R - CHART";
//   merge(r, 17, r + 1, 19).value = "HISTOGRAM";
//   [1, 14, 17].forEach((c) => {
//     const cell = ws.getCell(r, c);
//     cell.font = { name: "Arial", size: 10, bold: true };
//     cell.border = { ...border, left: c === 1 ? dbl : thin };
//   });

//   merge(r, 20, r, 21).value = "USL";
//   set(r, 22, usl);
//   merge(r, 23, r + 1, 23).value = "DIFF.";
//   merge(r, 24, r + 1, 24).value = diff;
//   merge(r, 25, r + 1, 25).value = "RANGE FOR 12";
//   merge(r, 26, r + 1, 26).value = Math.round(rangeFor12 * 1e6) / 1e6;
//   merge(r + 1, 20, r + 1, 21).value = "LSL";
//   set(r + 1, 22, lsl);

//   // sub-header row (row 9): UCL/Xbar/LCL | UCL/r/LCL | RANGE/FREQ
//   r = 9;
//   ["UCL", "Xbar", "LCL"].forEach((h, i) =>
//     set(r, 1 + i, h, { font: boldFont, fill: yellow }),
//   );
//   ["UCL", "r", "LCL"].forEach((h, i) =>
//     set(r, 14 + i, h, { font: boldFont, fill: yellow }),
//   );
//   merge(r, 17, r + 1, 17).value = "RANGE";
//   merge(r, 19, r + 1, 19).value = "FREQ.";
//   ws.getCell(r, 17).font = boldFont;
//   ws.getCell(r, 19).font = boldFont;
//   r = 10;
//   set(r, 17, "FIRST", { font: boldFont, fill: yellow });
//   set(r, 18, "LAST", { font: boldFont, fill: yellow });

//   // ============================================================
//   // ROWS 10-34 (25 rows): X-Chart + R-Chart data, Histogram bins (10-22)
//   // ============================================================
//   const xRows = spc.xChartRows || [];
//   const rRows = spc.rChartRows || [];
//   const bins = spc.histogram?.bins || [];
//   for (let i = 0; i < 25; i++) {
//     const row = 10 + i;
//     if (xRows[i]) {
//       set(row, 1, round3(xRows[i].ucl), { border: { ...border, left: dbl } });
//       set(row, 2, round3(xRows[i].xBar));
//       set(row, 3, round3(xRows[i].lcl));
//     }
//     if (rRows[i]) {
//       set(row, 14, round3(rRows[i].ucl));
//       set(row, 15, round3(rRows[i].r));
//       set(row, 16, round3(rRows[i].lcl));
//     }
//     if (bins[i]) {
//       set(row, 17, round3(bins[i].first));
//       set(row, 18, round3(bins[i].last));
//       set(row, 19, bins[i].freq);
//     }
//   }

//   // ============================================================
//   // CALCULATION FOR Cp/Cpk block (cols 20-26, starting row 23)
//   // ============================================================
//   merge(23, 20, 24, 26).value = "CALCULATION FOR Cp/Cpk";
//   ws.getCell(23, 20).font = { name: "Arial", size: 14, bold: true };
//   ws.getCell(23, 20).border = { ...border, right: dbl };
//   ws.getCell(23, 20).alignment = { horizontal: "center", vertical: "middle" };

//   merge(25, 20, 25, 26).value = `FOR  N - SAMPLE SIZE = ${spc.constantsUsed.N}`;
//   ws.getCell(25, 20).font = cellFont;
//   ws.getCell(25, 20).alignment = { horizontal: "left", vertical: "middle" };
//   ws.getCell(25, 20).border = { ...border, right: dbl };

//   merge(26, 20, 27, 21).value = "CONSTANT :";
//   ws.getCell(26, 20).alignment = {
//     wrapText: true,
//     horizontal: "center",
//     vertical: "middle",
//   };
//   const constLabels = ["A2", "D3", "D4", "d2"];
//   constLabels.forEach((k, i) => {
//     set(26, 22 + i, k, { font: boldFont, fill: yellow });
//     set(27, 22 + i, spc.constantsUsed[k]);
//   });

//   merge(29, 20, 29, 21).value = " X  =";
//   merge(29, 22, 29, 23).value = round4(spc.xBarBar);
//   merge(29, 24, 29, 24).value = " R =";
//   merge(29, 25, 29, 26).value = round3(spc.rBar);

//   merge(31, 20, 31, 21).value = " s = R / d2";
//   set(31, 22, round3(spc.rBar));
//   merge(31, 23, 31, 24).value = round4(spc.sigma);
//   merge(31, 20 + 5, 31, 21 + 5).value = ` A2.R =   ${spc.constantsUsed.A2} X `;

//   merge(33, 20, 33, 21).value = " UCLX = X + A2.R";
//   merge(33, 22, 33, 23).value = round4(spc.controlLimits.xBarUCL);
//   merge(33, 24, 33, 25).value = " UCLR = D4 .R";
//   merge(33, 26, 33, 26).value = round4(spc.controlLimits.rUCL);

//   merge(35, 20, 35, 21).value = " LCLX = X - A2.R";
//   merge(35, 22, 35, 23).value = round4(spc.controlLimits.xBarLCL);
//   merge(35, 24, 35, 25).value = " LCLR = D3 .R";
//   merge(35, 26, 35, 26).value = round4(spc.controlLimits.rLCL);

//   // graphical UCL/LCL rows (left side, cols 1-2 and 14-15)
//   merge(35, 1, 35, 2).value = "Graphical X  UCL";
//   set(35, 3, round4(spc.controlLimits.xBarUCL), {
//     border: { ...border, left: dbl },
//   });
//   merge(36, 1, 36, 2).value = "Graphical X  LCL";
//   set(36, 3, round4(spc.controlLimits.xBarLCL), {
//     border: { ...border, left: dbl },
//   });
//   merge(35, 14, 35, 15).value = "Graphical R  UCL";
//   set(35, 16, round4(spc.controlLimits.rUCL));
//   merge(36, 14, 36, 15).value = "Graphical R  LCL";
//   set(36, 16, round4(spc.controlLimits.rLCL));

//   // ============================================================
//   // VERDICT BANNERS (rows 37-42)
//   // ============================================================
//   const cpColor = spc.cp >= 1.33 ? "FF1B7A3D" : "FFB00020";
//   const cpkColor = spc.cpk >= 1.33 ? "FF1B7A3D" : "FFB00020";

//   const cpBanner = merge(37, 1, 38, 10);
//   cpBanner.value =
//     spc.cpVerdict ||
//     `PROCESS CAPABILITY IS ${spc.cp >= 1.33 ? "GOOD" : "IMPROVEMENT REQUIRED"}`;
//   cpBanner.font = {
//     name: "Arial",
//     size: 18,
//     bold: true,
//     color: { argb: cpColor },
//   };
//   cpBanner.alignment = { horizontal: "center", vertical: "middle" };
//   cpBanner.border = { ...border, left: dbl };

//   merge(37, 11, 38, 13).value = "Cp";
//   ws.getCell(37, 11).font = { name: "Arial", size: 18, bold: true };
//   merge(37, 14, 38, 16).value = round3(spc.cp);
//   ws.getCell(37, 14).font = { name: "Arial", size: 16, bold: true };

//   merge(37, 17, 38, 19).value = " Cp = T/ 6s";
//   merge(37, 20, 38, 21).value = round3(spc.cp);
//   merge(37, 22, 38, 25).value = " T = TOTAL TOLERANCE  ( USL-LSL )";
//   merge(37, 26, 38, 26).value = diff;

//   const cpkBanner = merge(39, 1, 40, 10);
//   cpkBanner.value =
//     spc.cpkVerdict ||
//     `PROCESS CAPABILITY INDEX IS ${spc.cpk >= 1.33 ? "GOOD" : "IMPROVEMENT REQUIRED"}`;
//   cpkBanner.font = {
//     name: "Arial",
//     size: 18,
//     bold: true,
//     color: { argb: cpkColor },
//   };
//   cpkBanner.alignment = { horizontal: "center", vertical: "middle" };
//   cpkBanner.border = { ...border, left: dbl };

//   merge(39, 11, 40, 13).value = "Cpk";
//   ws.getCell(39, 11).font = { name: "Arial", size: 18, bold: true };
//   merge(39, 14, 40, 16).value = round3(spc.cpk);
//   ws.getCell(39, 14).font = { name: "Arial", size: 16, bold: true };

//   merge(39, 17, 40, 19).value = "Cpk = ( USL X - X )/ 3s";
//   merge(39, 20, 40, 21).value = round3(spc.cpk);
//   merge(39, 22, 42, 26).value =
//     "FOR Cpk TAKE MINIMUM VALUE FOR BOTH SIDE TOLERANCE & FOR SINGLE SIDE TOLERANCE DO NOT CONSIDER MINIMUM VALUE.";
//   ws.getCell(39, 22).alignment = { wrapText: true, vertical: "middle" };
//   ws.getCell(39, 22).font = { name: "Arial", size: 9 };

//   merge(41, 17, 42, 19).value = "Cpk = ( X - LSL X )/ 3s";
//   merge(41, 20, 42, 21).value = round3(spc.cpk);

//   // ============================================================
//   // RAW DATA GRID: # / SHIFT / DATE / X1-X5 / SX / X-bar / R (rows 43-53)
//   // ============================================================
//   const gridStart = 43;
//   set(gridStart, 1, "", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((_, i) => {
//     set(gridStart, i + 2, i + 1, { font: boldFont });
//   });

//   set(gridStart + 1, 1, "SHIFT", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((_, i) =>
//     set(gridStart + 1, i + 2, "1", { fill: green }),
//   );

//   set(gridStart + 2, 1, "DATE", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((_, i) =>
//     set(gridStart + 2, i + 2, date, { fill: green }),
//   );

//   for (let x = 0; x < 5; x++) {
//     set(gridStart + 3 + x, 1, `X${x + 1}`, {
//       border: { ...border, left: dbl },
//     });
//     spc.subgroups.forEach((sg, i) => {
//       set(gridStart + 3 + x, i + 2, sg.values[x] ?? "", {
//         fill: cyan,
//         font: { name: "Arial", size: 9 },
//         numFmt: "0.00",
//       });
//     });
//   }

//   set(gridStart + 8, 1, "SX", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((sg, i) => {
//     const sx =
//       sg.mean !== null
//         ? Math.round(sg.mean * sg.values.length * 1000) / 1000
//         : "";
//     set(gridStart + 8, i + 2, sx, { numFmt: "0.00" });
//   });

//   set(gridStart + 9, 1, "X", { border: { ...border, left: dbl } });
//   spc.subgroups.forEach((sg, i) => {
//     set(gridStart + 9, i + 2, sg.mean !== null ? round3(sg.mean) : "");
//   });

//   const lastGridRow = gridStart + 10;
//   set(lastGridRow, 1, "R", { border: { ...border, left: dbl, bottom: dbl } });
//   spc.subgroups.forEach((sg, i) => {
//     set(lastGridRow, i + 2, sg.range !== null ? round3(sg.range) : "", {
//       border: {
//         ...border,
//         bottom: dbl,
//         right: i === spc.subgroups.length - 1 ? dbl : thin,
//       },
//     });
//   });

//   // ============================================================
//   // FOOTER
//   // ============================================================
//   const footerRow = lastGridRow + 1;
//   merge(footerRow, 1, footerRow, lastCol).value =
//     "MP/QA/20                    Rev:00                        Eff.Date: 01.DEC.2015      ";
//   ws.getCell(footerRow, 1).font = { name: "Arial", size: 9 };
//   ws.getCell(footerRow, 1).alignment = { horizontal: "right" };
//   ws.getCell(footerRow, 1).border = {};

//   // ---- helpers ----
//   function round3(v) {
//     return v == null || v === "" ? "" : Math.round(v * 1000) / 1000;
//   }
//   function round4(v) {
//     return v == null || v === "" ? "" : Math.round(v * 10000) / 10000;
//   }

//   // ── Trigger download ──
//   const buf = await wb.xlsx.writeBuffer();
//   const filename = `SPC_${product.code}_${date}.xlsx`;
//   saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
// }

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * Exports the SPC view to an .xlsx laid out EXACTLY like the reference
 * "SPC_Housing.xlsx" sheet:
 *   Title block -> Header info (Part/Instrument/Machine + IMP note) ->
 *   X-Chart | R-Chart | Histogram (side by side, with USL/LSL/DIFF panel) ->
 *   Calculation for Cp/Cpk block -> Graphical UCL/LCL rows ->
 *   Verdict banners (Cp / Cpk) -> raw SHIFT/DATE/X1-X5/SX/X-bar/R grid (25 cols) ->
 *   footer.
 *
 * @param {object} data - the full spc-data API response { product, spc, meta, date }
 */
export async function exportSpcToExcel(data) {
  const { product, spc, meta, date } = data;
  if (!spc || spc.insufficientData) {
    throw new Error(
      "Cannot export — Cp/Cpk not yet calculated (need 50+ readings).",
    );
  }

  // ---- derive USL / LSL from the spec string (e.g. "370 ±10gm") ----
  // Falls back to product.usl / product.lsl if the API already provides them.
  function deriveLimits() {
    if (product.usl != null && product.lsl != null) {
      return { usl: Number(product.usl), lsl: Number(product.lsl) };
    }
    const spec = String(product.specification || "");
    const m = spec.match(/(-?\d+(?:\.\d+)?)\s*±\s*(-?\d+(?:\.\d+)?)/);
    if (m) {
      const nominal = parseFloat(m[1]);
      const tol = parseFloat(m[2]);
      return { usl: nominal + tol, lsl: nominal - tol };
    }
    // last resort: use the computed control limits so the sheet still renders
    return {
      usl: spc.controlLimits?.xBarUCL ?? 0,
      lsl: spc.controlLimits?.xBarLCL ?? 0,
    };
  }
  const { usl, lsl } = deriveLimits();
  const diff = usl - lsl;
  const rangeFor12 = diff / 12;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("SPC Study", {
    views: [{ showGridLines: false }],
  });

  // ---- style helpers ----
  const thin = { style: "thin", color: { argb: "FF888888" } };
  const dbl = { style: "double", color: { argb: "FF444444" } };
  const border = { top: thin, left: thin, bottom: thin, right: thin };
  const cellFont = { name: "Arial", size: 10 };
  const boldFont = { name: "Arial", size: 10, bold: true };
  const green = "FFCCFFCC";
  const cyan = "FFCCFFFF";
  const yellow = "FFFCF9DA";
  const impFill = "FFCCFFCC";

  const NUM_SUBGROUPS = spc.subgroups.length; // typically 25
  const lastCol = 1 + NUM_SUBGROUPS; // col A = label, then one per subgroup

  // column widths: A narrow label col, B..Z ~ data cols
  ws.getColumn(1).width = 10;
  for (let c = 2; c <= lastCol; c++) ws.getColumn(c).width = 9;

  function set(row, col, value, opts = {}) {
    const cell = ws.getCell(row, col);
    cell.value = value;
    cell.font = opts.font || cellFont;
    cell.border = opts.border || border;
    cell.alignment = opts.align || { horizontal: "center", vertical: "middle" };
    if (opts.fill) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: opts.fill },
      };
    }
    if (opts.numFmt) cell.numFmt = opts.numFmt;
    return cell;
  }

  function merge(r1, c1, r2, c2) {
    ws.mergeCells(r1, c1, r2, c2);
    return ws.getCell(r1, c1);
  }

  // ============================================================
  // TITLE BLOCK (rows 1-3)
  // ============================================================
  merge(1, 1, 3, 4); // decorative corner box
  ws.getCell(1, 1).border = { top: dbl, left: dbl, bottom: thin, right: thin };

  const titleCell = merge(1, 5, 3, 22);
  titleCell.value = "STATISTICAL PROCESS CONTROL STUDY";
  titleCell.font = { name: "Arial", size: 20, bold: true };
  titleCell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  titleCell.border = { top: dbl, bottom: thin, left: thin, right: thin };

  merge(1, 23, 1, 26).value = `FORMAT No.:-  ${meta.formatNo || "-"}`;
  merge(2, 23, 2, 26).value = `CL.No.   : ${meta.clNo || "-"}`;
  merge(3, 23, 3, 26).value = `Page No. ${meta.pageNo || "1 of 1"}`;
  [1, 2, 3].forEach((r) => {
    const c = ws.getCell(r, 23);
    c.font = cellFont;
    c.alignment = { horizontal: "left", vertical: "middle" };
    c.border = {
      top: r === 1 ? dbl : thin,
      bottom: thin,
      left: thin,
      right: dbl,
    };
  });

  // ============================================================
  // HEADER INFO BLOCK (rows 4-6)
  // ============================================================
  const headerRows = [
    [
      "PART NAME:",
      product.name,
      "INSTRUMENT:",
      meta.instrument,
      "L.COUNT:",
      meta.leastCount,
    ],
    [
      "PART NO.:",
      product.code,
      "SPECIFICATION:",
      product.specification,
      "MACHINE:",
      meta.machine,
    ],
    [
      `SAMPLE SIZE:`,
      `${spc.count}`,
      "OPERATION:",
      meta.operation,
      "DOC.NO./DATE:",
      date,
    ],
  ];
  let r = 4;
  headerRows.forEach((row) => {
    // label/value pairs: A:B label, C:F value | G:H label, I:L value | M:N label, O:S value
    merge(r, 1, r, 2).value = row[0];
    merge(r, 3, r, 6).value = row[1];
    merge(r, 7, r, 8).value = row[2];
    merge(r, 9, r, 12).value = row[3];
    merge(r, 13, r, 14).value = row[4];
    merge(r, 15, r, 19).value = row[5];
    [1, 3, 7, 9, 13, 15].forEach((c) => {
      const cell = ws.getCell(r, c);
      cell.font = c === 3 || c === 9 || c === 15 ? cellFont : boldFont;
      cell.border = { ...border, left: c === 1 ? dbl : thin };
      cell.alignment = { horizontal: "left", vertical: "middle" };
      if (c === 3 || c === 9 || c === 15) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: cyan },
        };
      }
    });
    r++;
  });
  // IMP note block, spans the header rows on the right
  const impCell = merge(4, 20, 6, 26);
  impCell.value = meta.impNote || "";
  impCell.font = { name: "Arial", size: 9, italic: true };
  impCell.alignment = { wrapText: true, vertical: "top", horizontal: "left" };
  impCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: impFill },
  };
  impCell.border = { top: thin, bottom: thin, left: thin, right: dbl };

  // ============================================================
  // ROW 7-8: X-CHART / R-CHART / HISTOGRAM headers + USL/LSL panel
  // ============================================================
  r = 7;
  merge(r, 1, r + 1, 3).value = "X - CHART";
  merge(r, 14, r + 1, 16).value = "R - CHART";
  merge(r, 17, r + 1, 19).value = "HISTOGRAM";
  [1, 14, 17].forEach((c) => {
    const cell = ws.getCell(r, c);
    cell.font = { name: "Arial", size: 10, bold: true };
    cell.border = { ...border, left: c === 1 ? dbl : thin };
  });

  merge(r, 20, r, 21).value = "USL";
  set(r, 22, usl);
  merge(r, 23, r + 1, 23).value = "DIFF.";
  merge(r, 24, r + 1, 24).value = diff;
  merge(r, 25, r + 1, 25).value = "RANGE FOR 12";
  merge(r, 26, r + 1, 26).value = Math.round(rangeFor12 * 1e6) / 1e6;
  merge(r + 1, 20, r + 1, 21).value = "LSL";
  set(r + 1, 22, lsl);

  // sub-header row (row 9): UCL/Xbar/LCL | UCL/r/LCL | RANGE/FREQ
  r = 9;
  ["UCL", "Xbar", "LCL"].forEach((h, i) =>
    set(r, 1 + i, h, { font: boldFont, fill: yellow }),
  );
  ["UCL", "r", "LCL"].forEach((h, i) =>
    set(r, 14 + i, h, { font: boldFont, fill: yellow }),
  );
  merge(r, 17, r + 1, 17).value = "RANGE";
  merge(r, 19, r + 1, 19).value = "FREQ.";
  ws.getCell(r, 17).font = boldFont;
  ws.getCell(r, 19).font = boldFont;
  r = 10;
  set(r, 17, "FIRST", { font: boldFont, fill: yellow });
  set(r, 18, "LAST", { font: boldFont, fill: yellow });

  // ============================================================
  // ROWS 10-34 (25 rows): X-Chart + R-Chart data, Histogram bins (10-22)
  // ============================================================
  const xRows = spc.xChartRows || [];
  const rRows = spc.rChartRows || [];
  const bins = spc.histogram?.bins || [];
  for (let i = 0; i < 25; i++) {
    const row = 10 + i;
    if (xRows[i]) {
      set(row, 1, round3(xRows[i].ucl), { border: { ...border, left: dbl } });
      set(row, 2, round3(xRows[i].xBar));
      set(row, 3, round3(xRows[i].lcl));
    }
    if (rRows[i]) {
      set(row, 14, round3(rRows[i].ucl));
      set(row, 15, round3(rRows[i].r));
      set(row, 16, round3(rRows[i].lcl));
    }
    if (bins[i]) {
      set(row, 17, round3(bins[i].first));
      set(row, 18, round3(bins[i].last));
      set(row, 19, bins[i].freq);
    }
  }

  // ============================================================
  // CALCULATION FOR Cp/Cpk block (cols 20-26, starting row 23)
  // ============================================================
  merge(23, 20, 24, 26).value = "CALCULATION FOR Cp/Cpk";
  ws.getCell(23, 20).font = { name: "Arial", size: 14, bold: true };
  ws.getCell(23, 20).border = { ...border, right: dbl };
  ws.getCell(23, 20).alignment = { horizontal: "center", vertical: "middle" };

  merge(25, 20, 25, 26).value = `FOR  N - SAMPLE SIZE = ${spc.constantsUsed.N}`;
  ws.getCell(25, 20).font = cellFont;
  ws.getCell(25, 20).alignment = { horizontal: "left", vertical: "middle" };
  ws.getCell(25, 20).border = { ...border, right: dbl };

  merge(26, 20, 27, 21).value = "CONSTANT :";
  ws.getCell(26, 20).alignment = {
    wrapText: true,
    horizontal: "center",
    vertical: "middle",
  };
  const constLabels = ["A2", "D3", "D4", "d2"];
  constLabels.forEach((k, i) => {
    set(26, 22 + i, k, { font: boldFont, fill: yellow });
    set(27, 22 + i, spc.constantsUsed[k]);
  });

  merge(29, 20, 29, 21).value = " X  =";
  merge(29, 22, 29, 23).value = round4(spc.xBarBar);
  merge(29, 24, 29, 24).value = " R =";
  merge(29, 25, 29, 26).value = round3(spc.rBar);

  merge(31, 20, 31, 21).value = " s = R / d2";
  set(31, 22, round3(spc.rBar));
  merge(31, 23, 31, 24).value = round4(spc.sigma);
  merge(31, 20 + 5, 31, 21 + 5).value = ` A2.R =   ${spc.constantsUsed.A2} X `;

  merge(33, 20, 33, 21).value = " UCLX = X + A2.R";
  merge(33, 22, 33, 23).value = round4(spc.controlLimits.xBarUCL);
  merge(33, 24, 33, 25).value = " UCLR = D4 .R";
  merge(33, 26, 33, 26).value = round4(spc.controlLimits.rUCL);

  merge(35, 20, 35, 21).value = " LCLX = X - A2.R";
  merge(35, 22, 35, 23).value = round4(spc.controlLimits.xBarLCL);
  merge(35, 24, 35, 25).value = " LCLR = D3 .R";
  merge(35, 26, 35, 26).value = round4(spc.controlLimits.rLCL);

  // graphical UCL/LCL rows (left side, cols 1-2 and 14-15)
  merge(35, 1, 35, 2).value = "Graphical X  UCL";
  set(35, 3, round4(spc.controlLimits.xBarUCL), {
    border: { ...border, left: dbl },
  });
  merge(36, 1, 36, 2).value = "Graphical X  LCL";
  set(36, 3, round4(spc.controlLimits.xBarLCL), {
    border: { ...border, left: dbl },
  });
  merge(35, 14, 35, 15).value = "Graphical R  UCL";
  set(35, 16, round4(spc.controlLimits.rUCL));
  merge(36, 14, 36, 15).value = "Graphical R  LCL";
  set(36, 16, round4(spc.controlLimits.rLCL));

  // ============================================================
  // VERDICT BANNERS (rows 37-42)
  // ============================================================
  const cpColor = spc.cp >= 1.33 ? "FF1B7A3D" : "FFB00020";
  const cpkColor = spc.cpk >= 1.33 ? "FF1B7A3D" : "FFB00020";

  const cpBanner = merge(37, 1, 38, 10);
  cpBanner.value =
    spc.cpVerdict ||
    `PROCESS CAPABILITY IS ${spc.cp >= 1.33 ? "GOOD" : "IMPROVEMENT REQUIRED"}`;
  cpBanner.font = {
    name: "Arial",
    size: 18,
    bold: true,
    color: { argb: cpColor },
  };
  cpBanner.alignment = { horizontal: "center", vertical: "middle" };
  cpBanner.border = { ...border, left: dbl };

  merge(37, 11, 38, 13).value = "Cp";
  ws.getCell(37, 11).font = { name: "Arial", size: 18, bold: true };
  merge(37, 14, 38, 16).value = round3(spc.cp);
  ws.getCell(37, 14).font = { name: "Arial", size: 16, bold: true };

  merge(37, 17, 38, 19).value = " Cp = T/ 6s";
  merge(37, 20, 38, 21).value = round3(spc.cp);
  merge(37, 22, 38, 25).value = " T = TOTAL TOLERANCE  ( USL-LSL )";
  merge(37, 26, 38, 26).value = diff;

  const cpkBanner = merge(39, 1, 40, 10);
  cpkBanner.value =
    spc.cpkVerdict ||
    `PROCESS CAPABILITY INDEX IS ${spc.cpk >= 1.33 ? "GOOD" : "IMPROVEMENT REQUIRED"}`;
  cpkBanner.font = {
    name: "Arial",
    size: 18,
    bold: true,
    color: { argb: cpkColor },
  };
  cpkBanner.alignment = { horizontal: "center", vertical: "middle" };
  cpkBanner.border = { ...border, left: dbl };

  merge(39, 11, 40, 13).value = "Cpk";
  ws.getCell(39, 11).font = { name: "Arial", size: 18, bold: true };
  merge(39, 14, 40, 16).value = round3(spc.cpk);
  ws.getCell(39, 14).font = { name: "Arial", size: 16, bold: true };

  merge(39, 17, 40, 19).value = "Cpk = ( USL X - X )/ 3s";
  merge(39, 20, 40, 21).value = round3(spc.cpk);
  merge(39, 22, 42, 26).value =
    "FOR Cpk TAKE MINIMUM VALUE FOR BOTH SIDE TOLERANCE & FOR SINGLE SIDE TOLERANCE DO NOT CONSIDER MINIMUM VALUE.";
  ws.getCell(39, 22).alignment = { wrapText: true, vertical: "middle" };
  ws.getCell(39, 22).font = { name: "Arial", size: 9 };

  merge(41, 17, 42, 19).value = "Cpk = ( X - LSL X )/ 3s";
  merge(41, 20, 42, 21).value = round3(spc.cpk);

  // ============================================================
  // RAW DATA GRID: # / SHIFT / DATE / X1-X5 / SX / X-bar / R (rows 43-53)
  // ============================================================
  const gridStart = 43;
  set(gridStart, 1, "", { border: { ...border, left: dbl } });
  spc.subgroups.forEach((_, i) => {
    set(gridStart, i + 2, i + 1, { font: boldFont });
  });

  set(gridStart + 1, 1, "SHIFT", { border: { ...border, left: dbl } });
  spc.subgroups.forEach((_, i) =>
    set(gridStart + 1, i + 2, "1", { fill: green }),
  );

  set(gridStart + 2, 1, "DATE", { border: { ...border, left: dbl } });
  spc.subgroups.forEach((_, i) =>
    set(gridStart + 2, i + 2, date, { fill: green }),
  );

  for (let x = 0; x < 5; x++) {
    set(gridStart + 3 + x, 1, `X${x + 1}`, {
      border: { ...border, left: dbl },
    });
    spc.subgroups.forEach((sg, i) => {
      set(gridStart + 3 + x, i + 2, sg.values[x] ?? "", {
        fill: cyan,
        font: { name: "Arial", size: 9 },
        numFmt: "0.00",
      });
    });
  }

  set(gridStart + 8, 1, "SX", { border: { ...border, left: dbl } });
  spc.subgroups.forEach((sg, i) => {
    const sx =
      sg.mean !== null
        ? Math.round(sg.mean * sg.values.length * 1000) / 1000
        : "";
    set(gridStart + 8, i + 2, sx, { numFmt: "0.00" });
  });

  set(gridStart + 9, 1, "X", { border: { ...border, left: dbl } });
  spc.subgroups.forEach((sg, i) => {
    set(gridStart + 9, i + 2, sg.mean !== null ? round3(sg.mean) : "");
  });

  const lastGridRow = gridStart + 10;
  set(lastGridRow, 1, "R", { border: { ...border, left: dbl, bottom: dbl } });
  spc.subgroups.forEach((sg, i) => {
    set(lastGridRow, i + 2, sg.range !== null ? round3(sg.range) : "", {
      border: {
        ...border,
        bottom: dbl,
        right: i === spc.subgroups.length - 1 ? dbl : thin,
      },
    });
  });

  // ============================================================
  // FOOTER
  // ============================================================
  const footerRow = lastGridRow + 1;
  merge(footerRow, 1, footerRow, lastCol).value =
    "MP/QA/20                    Rev:00                        Eff.Date: 01.DEC.2015      ";
  ws.getCell(footerRow, 1).font = { name: "Arial", size: 9 };
  ws.getCell(footerRow, 1).alignment = { horizontal: "right" };
  ws.getCell(footerRow, 1).border = {};

  // ---- helpers ----
  function round3(v) {
    return v == null || v === "" ? "" : Math.round(v * 1000) / 1000;
  }
  function round4(v) {
    return v == null || v === "" ? "" : Math.round(v * 10000) / 10000;
  }

  // ********************
  // ── Embedded SPC charts: ONLY added into the existing blank chart areas ──
  async function renderCanvasPng(width, height, draw) {
    if (typeof document === "undefined") {
      throw new Error("Chart rendering requires a browser environment.");
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Unable to create chart canvas.");
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    draw(ctx, width, height);

    return canvas.toDataURL("image/png");
  }

  function drawLineChart(
    ctx,
    width,
    height,
    title,
    rows,
    valueKey,
    uclKey,
    lclKey,
  ) {
    const margin = {
      top: 48,
      right: 20,
      bottom: 38,
      left: 58,
    };

    const plotW = width - margin.left - margin.right;
    const plotH = height - margin.top - margin.bottom;

    const cleanRows = (rows || []).filter(
      (row) =>
        Number.isFinite(Number(row?.[valueKey])) ||
        Number.isFinite(Number(row?.[uclKey])) ||
        Number.isFinite(Number(row?.[lclKey])),
    );

    if (!cleanRows.length) return;

    const values = cleanRows.flatMap((row) =>
      [row?.[valueKey], row?.[uclKey], row?.[lclKey]]
        .map(Number)
        .filter(Number.isFinite),
    );

    let min = Math.min(...values);
    let max = Math.max(...values);

    const span = max - min;

    const pad = span === 0 ? Math.max(Math.abs(max) * 0.02, 1) : span * 0.12;

    min -= pad;
    max += pad;

    const x = (i) =>
      margin.left +
      (cleanRows.length <= 1
        ? plotW / 2
        : (i / (cleanRows.length - 1)) * plotW);

    const y = (v) => margin.top + ((max - v) / (max - min)) * plotH;

    // Grid
    ctx.strokeStyle = "#d9d9d9";
    ctx.lineWidth = 1;

    ctx.font = "11px Arial";
    ctx.fillStyle = "#444";

    ctx.textAlign = "right";
    ctx.textBaseline = "middle";

    for (let i = 0; i <= 5; i++) {
      const value = min + ((max - min) * i) / 5;

      const yy = y(value);

      ctx.beginPath();
      ctx.moveTo(margin.left, yy);
      ctx.lineTo(width - margin.right, yy);
      ctx.stroke();

      ctx.fillText(value.toFixed(2), margin.left - 7, yy);
    }

    // Axis
    ctx.strokeStyle = "#555";

    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, height - margin.bottom);

    ctx.lineTo(width - margin.right, height - margin.bottom);

    ctx.stroke();

    // X-axis labels
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const labelStep = Math.max(1, Math.ceil(cleanRows.length / 12));

    for (let i = 0; i < cleanRows.length; i += labelStep) {
      ctx.fillText(String(i + 1), x(i), height - margin.bottom + 7);
    }

    const series = [
      {
        key: uclKey,
        label: "UCL",
        color: "#d62728",
      },
      {
        key: valueKey,
        label: valueKey === "xBar" ? "Xbar" : "R",
        color: "#1f77b4",
      },
      {
        key: lclKey,
        label: "LCL",
        color: "#d62728",
      },
    ];

    series.forEach((seriesItem) => {
      ctx.strokeStyle = seriesItem.color;

      ctx.lineWidth = seriesItem.key === valueKey ? 2.2 : 1.5;

      ctx.beginPath();

      let started = false;

      cleanRows.forEach((row, i) => {
        const value = Number(row?.[seriesItem.key]);

        if (!Number.isFinite(value)) return;

        const px = x(i);
        const py = y(value);

        if (!started) {
          ctx.moveTo(px, py);
          started = true;
        } else {
          ctx.lineTo(px, py);
        }
      });

      ctx.stroke();
    });

    // Title
    ctx.fillStyle = "#111";

    ctx.font = "bold 16px Arial";

    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    ctx.fillText(title, width / 2, 10);

    // Legend
    ctx.font = "11px Arial";
    ctx.textAlign = "left";

    series.forEach((item, i) => {
      const lx = margin.left + i * 95;

      ctx.fillStyle = item.color;

      ctx.fillRect(lx, 33, 18, 3);

      ctx.fillStyle = "#333";

      ctx.fillText(item.label, lx + 23, 28);
    });
  }

  function drawHistogram(ctx, width, height, title, bins) {
    const margin = {
      top: 48,
      right: 18,
      bottom: 58,
      left: 52,
    };

    const plotW = width - margin.left - margin.right;

    const plotH = height - margin.top - margin.bottom;

    const cleanBins = (bins || []).filter(
      (b) =>
        Number.isFinite(Number(b?.first)) && Number.isFinite(Number(b?.last)),
    );

    if (!cleanBins.length) return;

    const frequencies = cleanBins.map((b) => Math.max(0, Number(b?.freq) || 0));

    const maxFreq = Math.max(...frequencies, 1);

    const slotW = plotW / cleanBins.length;

    const barW = Math.max(2, slotW - 3);

    const y = (v) => margin.top + plotH - (v / maxFreq) * plotH;

    // Grid
    ctx.strokeStyle = "#d9d9d9";
    ctx.lineWidth = 1;

    ctx.font = "11px Arial";
    ctx.fillStyle = "#444";

    ctx.textAlign = "right";
    ctx.textBaseline = "middle";

    for (let i = 0; i <= 5; i++) {
      const value = (maxFreq * i) / 5;

      const yy = y(value);

      ctx.beginPath();

      ctx.moveTo(margin.left, yy);

      ctx.lineTo(width - margin.right, yy);

      ctx.stroke();

      ctx.fillText(String(Math.round(value)), margin.left - 7, yy);
    }

    // Axis
    ctx.strokeStyle = "#555";

    ctx.beginPath();

    ctx.moveTo(margin.left, margin.top);

    ctx.lineTo(margin.left, height - margin.bottom);

    ctx.lineTo(width - margin.right, height - margin.bottom);

    ctx.stroke();

    // Bars
    cleanBins.forEach((bin, i) => {
      const freq = frequencies[i];

      const bx = margin.left + i * slotW + (slotW - barW) / 2;

      const by = y(freq);

      ctx.fillStyle = "#5b8def";

      ctx.fillRect(bx, by, barW, height - margin.bottom - by);
    });

    // Bin labels
    ctx.fillStyle = "#333";
    ctx.font = "9px Arial";

    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const labelStep = Math.max(1, Math.ceil(cleanBins.length / 8));

    for (let i = 0; i < cleanBins.length; i += labelStep) {
      const bin = cleanBins[i];

      const label = `${round3(Number(bin.first))}-${round3(Number(bin.last))}`;

      const bx = margin.left + i * slotW + slotW / 2;

      ctx.save();

      ctx.translate(bx, height - margin.bottom + 7);

      ctx.rotate(-Math.PI / 6);

      ctx.fillText(label, 0, 0);

      ctx.restore();
    }

    // Title
    ctx.fillStyle = "#111";

    ctx.font = "bold 16px Arial";

    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    ctx.fillText(title, width / 2, 10);
  }

  // ============================================================
  // USE EXISTING SPC DATA
  // ============================================================

  const xRowsForChart = spc.xChartRows || [];

  const rRowsForChart = spc.rChartRows || [];

  const histogramBinsForChart = spc.histogram?.bins || [];

  // ============================================================
  // X-CHART
  // ============================================================

  const xChartPng = await renderCanvasPng(900, 360, (ctx, w, h) =>
    drawLineChart(ctx, w, h, "X - CHART", xRowsForChart, "xBar", "ucl", "lcl"),
  );

  // ============================================================
  // R-CHART
  // ============================================================

  const rChartPng = await renderCanvasPng(900, 360, (ctx, w, h) =>
    drawLineChart(ctx, w, h, "R - CHART", rRowsForChart, "r", "ucl", "lcl"),
  );

  // ============================================================
  // HISTOGRAM
  // ============================================================

  const histogramPng = await renderCanvasPng(720, 360, (ctx, w, h) =>
    drawHistogram(ctx, w, h, "HISTOGRAM", histogramBinsForChart),
  );

  // ============================================================
  // ADD IMAGES TO EXCEL
  // ============================================================

  const xChartImageId = wb.addImage({
    base64: xChartPng,
    extension: "png",
  });

  const rChartImageId = wb.addImage({
    base64: rChartPng,
    extension: "png",
  });

  const histogramImageId = wb.addImage({
    base64: histogramPng,
    extension: "png",
  });

  // ============================================================
  // IMPORTANT:
  // ONLY USE THE EXISTING BLANK GRAPH AREAS
  // ============================================================

  ws.addImage(xChartImageId, "D7:M21");

  ws.addImage(rChartImageId, "D22:M36");

  ws.addImage(histogramImageId, "T9:Z22");
  // ******************

  // ── Trigger download ──
  const buf = await wb.xlsx.writeBuffer();
  const filename = `SPC_${product.code}_${date}.xlsx`;
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
}

/**
 * Exports a plain table of Weight History entries for a selected date+time
 * range (optionally a single product / single result) — used by the
 * "Export Excel" button on the Weight History page. A summary row up top
 * states exactly what filters produced this file, then one row per entry,
 * matching the columns shown on-screen.
 *
 * @param {Array} entries - weight entries for the selected range (already
 *   fetched with no pagination limit, so this is the FULL range's data)
 * @param {object} range
 * @param {string} range.from - "YYYY-MM-DD HH:mm:ss", range start
 * @param {string} range.to - "YYYY-MM-DD HH:mm:ss", range end
 * @param {string|null} [range.productName] - selected product, null = all
 * @param {string|null} [range.result] - "Pass" | "Fail", null = both
 */
export async function exportWeightHistoryToExcel(entries, range) {
  const { from, to, productName, result } = range || {};
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Weight History");

  // ── Summary row — makes it obvious what was exported when the file
  // is opened later, without needing to re-check the app's filters.
  const summaryParts = [`Range: ${from} to ${to}`];
  summaryParts.push(`Part: ${productName || "All"}`);
  summaryParts.push(`Result: ${result || "All"}`);
  summaryParts.push(`Total entries: ${entries.length}`);
  ws.mergeCells("A1:H1");
  const summaryCell = ws.getCell("A1");
  summaryCell.value = summaryParts.join("   |   ");
  summaryCell.font = { bold: true, size: 11 };
  ws.addRow([]);

  const headerRowIndex = 3;
  ws.getRow(headerRowIndex).values = [
    "Part ID",
    "Part Name",
    "Weight (g)",
    "Range (g)",
    "Result",
    "Serial Number",
    "Date",
    "Time",
  ];
  ws.columns = [
    { key: "product_code", width: 14 },
    { key: "product_name", width: 26 },
    { key: "weight", width: 12 },
    { key: "range", width: 16 },
    { key: "result", width: 10 },
    { key: "serial_number", width: 20 },
    { key: "date", width: 14 },
    { key: "time", width: 12 },
  ];

  ws.getRow(headerRowIndex).font = { bold: true };
  ws.getRow(headerRowIndex).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  entries.forEach((e) => {
    const recorded = new Date(e.recorded_at);
    ws.addRow({
      product_code: e.product_code,
      product_name: e.product_name,
      weight: e.weight,
      range: `${e.min_weight}–${e.max_weight}`,
      result: e.result,
      serial_number: e.serial_number || "—",
      date: recorded.toLocaleDateString(),
      time: recorded.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    });
  });

  // Highlight Fail rows so they stand out in the exported sheet
  ws.eachRow((row, rowNumber) => {
    if (rowNumber <= headerRowIndex) return;
    const resultCell = row.getCell(5);
    if (resultCell.value === "Fail") {
      row.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFD6D6" },
        };
      });
    }
  });

  const buf = await wb.xlsx.writeBuffer();
  const fromDatePart = (from || "").split(" ")[0];
  const toDatePart = (to || "").split(" ")[0];
  const filename =
    fromDatePart === toDatePart
      ? `WeightHistory_${fromDatePart}.xlsx`
      : `WeightHistory_${fromDatePart}_to_${toDatePart}.xlsx`;
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
}
